import { useNavigate, useLocation } from 'react-router-dom'
import styles from './Footer.module.css'

// ── ICONS ─────────────────────────────────────────────────────────────────────
const BotIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2"/>
    <circle cx="12" cy="5" r="2"/>
    <path d="M12 7v4"/>
    <circle cx="8"  cy="16" r="1.2" fill="currentColor" stroke="none"/>
    <circle cx="16" cy="16" r="1.2" fill="currentColor" stroke="none"/>
  </svg>
)

const ZapIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
)

const ShieldIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
)

const GlobeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
)

const HeartIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
)

const ArrowUpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5"/>
    <polyline points="5 12 12 5 19 12"/>
  </svg>
)

const FOOTER_LINKS = {
  Platform: [
    { label: 'Deploy Session', to: '/' },
    { label: 'Server Status',  to: '/servers' },
    { label: 'API Endpoint',   to: '/' },
  ],
  Company: [
    { label: 'Privacy Policy', to: '/privacy' },
    { label: 'Contact Us',     to: '/contact' },
  ],
  Features: [
    { label: 'Media Downloads',  to: '/privacy' },
    { label: 'Group Management', to: '/privacy' },
    { label: 'Auto Status Seen', to: '/privacy' },
    { label: 'Auto Reply',       to: '/privacy' },
  ],
}

export default function Footer({ totalBots, onlineCount, serverCount }) {
  const navigate  = useNavigate()
  const isOnline  = onlineCount > 0

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

  return (
    <footer className={styles.footer}>
      {/* TOP BORDER SHIMMER */}
      <div className={styles.topShimmer} />

      <div className={styles.inner}>

        {/* ── TOP ROW ── */}
        <div className={styles.top}>

          {/* BRAND */}
          <div className={styles.brand}>
            <div className={styles.brandLogo} onClick={() => navigate('/')}>
              <div className={styles.brandMark}><BotIcon /></div>
              <div className={styles.brandName}>
                <span>SAVIYA</span><span className={styles.brandAccent}>-X</span>
              </div>
            </div>
            <p className={styles.brandTagline}>
              Distributed WhatsApp bot automation.<br />
              Free forever. Zero data stored.
            </p>

            {/* LIVE STATUS CARD */}
            <div className={styles.liveCard}>
              <div className={styles.liveCardHeader}>
                <span className={`${styles.liveDot} ${isOnline ? styles.liveDotOn : styles.liveDotOff}`} />
                <span className={styles.liveCardTitle}>
                  {isOnline ? 'All systems operational' : 'Service degraded'}
                </span>
              </div>
              <div className={styles.liveStats}>
                <div className={styles.liveStat}>
                  <span className={styles.liveStatVal} style={{ color: 'var(--accent)' }}>{totalBots}</span>
                  <span className={styles.liveStatKey}>Active bots</span>
                </div>
                <div className={styles.liveStatDiv} />
                <div className={styles.liveStat}>
                  <span className={styles.liveStatVal} style={{ color: 'var(--green)' }}>{onlineCount}</span>
                  <span className={styles.liveStatKey}>Servers up</span>
                </div>
                <div className={styles.liveStatDiv} />
                <div className={styles.liveStat}>
                  <span className={styles.liveStatVal}>{serverCount}</span>
                  <span className={styles.liveStatKey}>Total nodes</span>
                </div>
              </div>
            </div>
          </div>

          {/* LINKS */}
          <div className={styles.linksGrid}>
            {Object.entries(FOOTER_LINKS).map(([group, links]) => (
              <div key={group} className={styles.linkGroup}>
                <div className={styles.linkGroupTitle}>{group}</div>
                <ul className={styles.linkList}>
                  {links.map((l) => (
                    <li key={l.label}>
                      <button className={styles.footerLink} onClick={() => navigate(l.to)}>
                        {l.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

        </div>

        {/* ── TRUST STRIP ── */}
        <div className={styles.trustStrip}>
          {[
            { icon: <ShieldIcon />, text: 'No data selling' },
            { icon: <ZapIcon />,    text: '100% Free forever' },
            { icon: <GlobeIcon />,  text: 'WhatsApp Web API' },
            { icon: <ShieldIcon />, text: 'Zero data stored' },
            { icon: <ZapIcon />,    text: 'Instant deployment' },
            { icon: <ShieldIcon />, text: 'No hacking · No spam' },
          ].map((t) => (
            <div key={t.text} className={styles.trustItem}>
              <span className={styles.trustIcon}>{t.icon}</span>
              {t.text}
            </div>
          ))}
        </div>

        {/* ── BOTTOM BAR ── */}
        <div className={styles.bottom}>
          <div className={styles.bottomLeft}>
            <span className={styles.copyright}>
              © {new Date().getFullYear()} Saviya-X-Md. All rights reserved.
            </span>
      
          </div>

          <div className={styles.bottomRight}>
            <span className={styles.builtOn}>
              Powered by Savithu Induwara
            </span>
            <button className={styles.backTop} onClick={scrollTop} title="Back to top">
              <ArrowUpIcon />
            </button>
          </div>
        </div>

      </div>
    </footer>
  )
}
